import * as preact from "preact";

import readlangClient from "readlangClient";
import { cn } from "shared/utils";
import Dialog from "views/Dialog";

type HistoryDay = {
  date: string;
  dayOfWeek: string;
  wordsRead: number;
  wordsPracticed: number;
  streakRepair: number;
  fractionOfGoalMet: number;
  goalMet: boolean;
  isToday: boolean;
};

type StreakData = {
  username: string;
  subscriptionPlan: string;
  subscriptionExpiryDate: string | null;
  streakRepairs: number;
  currentStreak: number;
  wordsPerDayGoal: number;
  practiceWordEquivalent: number;
  history: HistoryDay[];
  lastMissedDate: string | null;
  projectedStreak: number | null;
};

type Props = {
  userID: string;
  email: string;
  onStreakUpdated: () => void;
};

type State = {
  data: StreakData | null;
  loading: boolean;
  repairing: boolean;
  error: string | null;
  repairedDate: string | null;
};

export default class StreakDialog extends preact.Component<Props, State> {
  state: State = {
    data: null,
    loading: true,
    repairing: false,
    error: null,
    repairedDate: null,
  };

  componentDidMount() {
    this.load();
  }

  async load() {
    this.setState({ loading: true, error: null });
    try {
      const response = await readlangClient.request(
        "GET",
        `/api/admin/user/${this.props.userID}/streak`,
        {}
      );
      this.setState({ data: response.data, loading: false });
    } catch (error: any) {
      this.setState({
        error: this.errorMessage(error, "Failed to load streak"),
        loading: false,
      });
    }
  }

  async repair() {
    const { data } = this.state;
    if (!data?.lastMissedDate) {
      return;
    }

    this.setState({ repairing: true, error: null });
    try {
      await readlangClient.request(
        "POST",
        `/api/admin/user/${this.props.userID}/repairStreakDay`,
        { date: data.lastMissedDate }
      );
      this.setState({ repairedDate: data.lastMissedDate, repairing: false });
      this.props.onStreakUpdated();
      await this.load();
    } catch (error: any) {
      this.setState({
        error: this.errorMessage(error, "Failed to repair streak"),
        repairing: false,
      });
    }
  }

  errorMessage(error: any, fallback: string) {
    console.error(fallback, error);
    if (error.jqXHR?.alreadyNotified) {
      return "Connection problem. Please try again.";
    }
    return (
      error.jqXHR?.data?.error ||
      error.jqXHR?.responseJSON?.error ||
      error.data?.error ||
      error.error ||
      fallback
    );
  }

  effectiveWords(day: HistoryDay, data: StreakData) {
    return day.wordsRead + day.wordsPracticed * data.practiceWordEquivalent;
  }

  render() {
    const { data, loading, repairing, error, repairedDate } = this.state;

    return (
      <Dialog class="streakDialog" heading="Streak" error={error}>
        <p>
          <strong>{this.props.email}</strong>
        </p>

        {loading && <p>Loading…</p>}

        {data && (
          <div>
            <ul class="streakSummary">
              <li>
                Current streak: <strong>{data.currentStreak}</strong> day
                {data.currentStreak === 1 ? "" : "s"}
              </li>
              <li>
                Plan: <strong>{data.subscriptionPlan}</strong>
              </li>
              <li>
                Streak repairs available: <strong>{data.streakRepairs}</strong>
              </li>
            </ul>

            <table class="streakTable">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Read</th>
                  <th>Practiced</th>
                  <th>Total / Goal</th>
                  <th>Met?</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((day) => {
                  const isMissed = day.date === data.lastMissedDate;
                  return (
                    <tr
                      class={cn({
                        missedDay: isMissed,
                        today: day.isToday,
                      })}
                    >
                      <td>
                        {day.dayOfWeek} {day.date}
                        {day.isToday ? " (today)" : ""}
                      </td>
                      <td>{day.wordsRead}</td>
                      <td>{day.wordsPracticed}</td>
                      <td>
                        {this.effectiveWords(day, data)} /{" "}
                        {data.wordsPerDayGoal}
                      </td>
                      <td>
                        {day.streakRepair > 0 ? "🔧" : day.goalMet ? "✓" : "✗"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {data.lastMissedDate ? (
              <div class="streakRepairAction">
                <p>
                  Last missed day: <strong>{data.lastMissedDate}</strong>.
                  Repairing it would make the streak{" "}
                  <strong>{data.projectedStreak}</strong> day
                  {data.projectedStreak === 1 ? "" : "s"}.
                </p>
                <button onClick={() => this.repair()} disabled={repairing}>
                  {repairing ? "Repairing…" : `Repair ${data.lastMissedDate}`}
                </button>
              </div>
            ) : (
              <p>No missed days in the past 7 days to repair.</p>
            )}

            {repairedDate && (
              <p class="streakRepaired">
                Repaired {repairedDate}. Streak is now {data.currentStreak} day
                {data.currentStreak === 1 ? "" : "s"}.
              </p>
            )}
          </div>
        )}
      </Dialog>
    );
  }
}
